import { useState, useEffect } from 'react';
import { Users, Contact, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/lib/supabase';
import { useWorkspaces } from '@/hooks/useWorkspaces';

interface SharedContact {
  id: string;
  contact_id: string;
  first_name: string;
  last_name: string;
  company_name: string | null;
  permissions: {
    view?: boolean;
    edit?: boolean;
    delete?: boolean;
    export?: boolean;
  };
}

interface SharedGroup {
  id: string;
  group_id: string;
  name: string;
  contact_count: number;
  permissions: {
    role?: string;
  };
}

interface MemberSharingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: {
    user_id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
  } | null;
}

export function MemberSharingDialog({ open, onOpenChange, member }: MemberSharingDialogProps) {
  const [contacts, setContacts] = useState<SharedContact[]>([]);
  const [groups, setGroups] = useState<SharedGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentWorkspace } = useWorkspaces();

  useEffect(() => {
    if (open && member && currentWorkspace) {
      fetchSharedItems();
    }
  }, [open, member?.user_id, currentWorkspace?.id]);

  const fetchSharedItems = async () => {
    if (!member || !currentWorkspace) return;

    setLoading(true);
    try {
      const [contactsResult, groupsResult] = await Promise.all([
        supabase
          .from('contact_shares')
          .select(`
            id,
            contact_id,
            permissions,
            contacts:contact_id (
              first_name,
              last_name,
              company_name
            )
          `)
          .eq('shared_with_user_id', member.user_id)
          .eq('workspace_id', currentWorkspace.id),
        supabase
          .from('group_shares')
          .select(`
            id,
            group_id,
            permissions,
            groups:group_id (
              name
            )
          `)
          .eq('shared_with_user_id', member.user_id)
          .eq('workspace_id', currentWorkspace.id),
      ]);

      if (contactsResult.error) throw contactsResult.error;
      if (groupsResult.error) throw groupsResult.error;

      const sharedContacts: SharedContact[] = (contactsResult.data || []).map((cs: any) => ({
        id: cs.id,
        contact_id: cs.contact_id,
        first_name: cs.contacts?.first_name || '',
        last_name: cs.contacts?.last_name || '',
        company_name: cs.contacts?.company_name || null,
        permissions: cs.permissions || {},
      }));

      const sharedGroups: SharedGroup[] = (groupsResult.data || []).map((gs: any) => ({
        id: gs.id,
        group_id: gs.group_id,
        name: gs.groups?.name || 'Unknown Group',
        contact_count: 0,
        permissions: gs.permissions || {},
      }));

      setContacts(sharedContacts);
      setGroups(sharedGroups);
    } catch (error) {
      console.error('Error fetching shared items:', error);
    } finally {
      setLoading(false);
    }
  };

  const memberName = member
    ? `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email
    : '';

  const permissionBadges = (perms: SharedContact['permissions']) => {
    const badges = [];
    if (perms.view) badges.push('View');
    if (perms.edit) badges.push('Edit');
    if (perms.delete) badges.push('Delete');
    if (perms.export) badges.push('Export');
    return badges;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Shared with {memberName}</DialogTitle>
          <DialogDescription>
            Contacts and groups shared with this member
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Shared Contacts */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Contact className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-medium">
                  Contacts ({contacts.length})
                </h3>
              </div>
              {contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground pl-6">
                  No contacts shared with this member
                </p>
              ) : (
                <div className="space-y-2 pl-6">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50"
                    >
                      <div>
                        <span className="text-sm font-medium">
                          {contact.first_name} {contact.last_name}
                        </span>
                        {contact.company_name && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {contact.company_name}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {permissionBadges(contact.permissions).map((perm) => (
                          <Badge key={perm} variant="outline" className="text-xs px-1.5 py-0">
                            {perm}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Shared Groups */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-medium">
                  Groups ({groups.length})
                </h3>
              </div>
              {groups.length === 0 ? (
                <p className="text-sm text-muted-foreground pl-6">
                  No groups shared with this member
                </p>
              ) : (
                <div className="space-y-2 pl-6">
                  {groups.map((group) => (
                    <div
                      key={group.id}
                      className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50"
                    >
                      <span className="text-sm font-medium">{group.name}</span>
                      <Badge variant="outline" className="text-xs px-1.5 py-0 capitalize">
                        {group.permissions.role || 'viewer'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {contacts.length === 0 && groups.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-4">
                Nothing has been shared with this member yet.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}